import React, { useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useSelector } from "react-redux";
import CabPage from "../CabPage";
import MobileNav from "../account/MobileNav";
import "../css/loads.css";
import "../css/unified.css";
import SearchHero, { dailySeries } from "../search/SearchHero";
import { FilterTabs, SearchFooter, VIEW_ICONS } from "../search/SearchChrome";
import useCompanies from "../search/useCompanies";
import CompanyLogo from "../search/CompanyLogo";
import { loadInfo } from "../search/mapInfo";
import MapPanel from "../search/MapPanel";
import { Modal } from "../search/ui";
import SearchFields from "../search/SearchFields";
import RespondPanel from "../loads/RespondPanel";
import { filterLoads } from "../loads/filterLoads";
import { LoadDetailsPanel } from "../ui/cards";
import RowMenu, { useHiddenRows, useRowMenu } from "../ui/RowMenu";
import { EMPTY_FILTERS, useMetrics, fmtDuration } from "../search/liveTrucks";
import { isCarrierRole } from "../useUnread";
import { fetchMyRequests, fetchMatchSettings } from "../../utils/matchingApi";
import {
  Icon,
  P,
  fetchAllOrders,
  notExpired,
  ttlActive,
  DEFAULT_LOAD_TTL_HOURS,
  cityOf,
  nf,
  money,
  fmtDateShort,
  bodyTypeName,
  ORDER_TYPES,
  currencyLabel,
} from "../loads/common";

/**
 * «Найти грузы» — wireframe iTruck_Loads_Search.html.
 * Data: POST /order_search/ (same call as legacy OrderSearchForm), only published
 * (status 1) and not expired loads; filtering/sorting is client-side exactly like
 * the legacy form. The search window is the same as on «Главная» / «Найти транспорт» (SearchFields, kind="loads").
 * «Откликнуться» opens the load's card in a side panel where the carrier offers one or several trucks
 * (POST /m/loads/:id/propose); `···` → «Детали груза» (with «Отправить заявку»); full-screen map; sort; ⌘K results.
 */
// company profile `role` (same labels as «Контрагенты»)
const COMPANY_ROLES = { 1: "Грузовладелец", 2: "Перевозчик", 3: "Экспедитор", 4: "Экспедитор-перевозчик" };
const routeOf = (o) => `${cityOf(o.from_address)} → ${cityOf(o.to_address)}`;
const loadText = (o) =>
  [o.from_address, o.to_address, o.from_country, o.to_country, o.comment, ORDER_TYPES[o.item?.type], bodyTypeName(o.transport_body_type), o.contacts?.company_name, o.contacts?.contact_name, o.uuid]
    .filter(Boolean)
    .join(" ")
    .toLowerCase();

export default function LoadsPage() {
  const [params, setParams] = useSearchParams();
  const q = (params.get("q") || "").trim();
  const user = useSelector((s) => s.auth?.user);
  const carrier = isCarrierRole(user?.role);

  const [orders, setOrders] = useState(null);
  const [allOrders, setAllOrders] = useState([]); // every order incl. closed ones: only for the «новых за день» series
  const { byOwner, all: companies } = useCompanies();
  const metrics = useMetrics(user?.role);
  const [error, setError] = useState(null);
  const [draft, setDraft] = useState(EMPTY_FILTERS);
  const [applied, setApplied] = useState(EMPTY_FILTERS);
  const [sort, setSort] = useState("new");
  const [matches, setMatches] = useState([]); // my trucks' matching requests (carriers)
  const [dialog, setDialog] = useState(null);
  const rowMenu = useRowMenu();
  const hid = useHiddenRows("loads", user?.id);
  const [toast, setToast] = useState("");
  const toastTimer = useRef(null);
  const resultsRef = useRef(null);

  const say = (msg) => {
    clearTimeout(toastTimer.current);
    setToast(msg);
    toastTimer.current = setTimeout(() => setToast(""), 4200);
  };
  useEffect(() => () => clearTimeout(toastTimer.current), []);

  useEffect(() => {
    if (!user?.id) return undefined;
    let alive = true;
    Promise.all([
      fetchAllOrders(),
      fetchMatchSettings().then((s) => Number(s?.loadTtlHours) || DEFAULT_LOAD_TTL_HOURS).catch(() => DEFAULT_LOAD_TTL_HOURS),
    ])
      .then(
        ([d, ttl]) => {
          if (!alive) return;
          setAllOrders(d);
          // published, date window not over, matching TTL not over, and never the viewer's own loads
          setOrders(d.filter((o) => o.status === 1 && notExpired(o) && ttlActive(o, ttl) && o.owner !== user.id));
        },
      )
      .catch(() => alive && setError("Не удалось загрузить грузы"));
    return () => {
      alive = false;
    };
  }, [user?.id]);

  const loadMatches = () =>
    carrier
      ? fetchMyRequests()
          .then((r) => setMatches(r?.matches || []))
          .catch(() => setMatches([]))
      : Promise.resolve();
  useEffect(() => {
    loadMatches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [carrier]);
  const matchFor = (o) => matches.filter((m) => m.load?.id === o.uuid);
  const responded = (o) => matchFor(o).some((m) => m.decided);
  const cities = useMemo(
    () => [...new Set((orders || []).flatMap((o) => [cityOf(o.from_address), cityOf(o.to_address)]).filter((c) => c && c !== "—"))].sort(),
    [orders],
  );

  const filtered = useMemo(() => {
    if (!orders) return [];
    const f = applied;
    let list = filterLoads(orders, f).filter((o) => !hid.isHidden(o.uuid)).filter((o) => !q || q.toLowerCase().split(/\s+/).every((w) => loadText(o).includes(w)));
    // wireframe sort: «По ставке» ascending by rate, «По дате погрузки» by date, else newest first
    list = [...list].sort((a, b) => {
      if (sort === "rate") return (a.price || 0) - (b.price || 0);
      if (sort === "date") return String(a.from_date || "9").localeCompare(String(b.from_date || "9"));
      return new Date(b.created_at) - new Date(a.created_at);
    });
    return list;
  }, [orders, applied, sort, q, hid.count, byOwner]); // eslint-disable-line react-hooks/exhaustive-deps

  const grams = (n) => (n % 10 === 1 && n % 100 !== 11 ? "груз" : n % 10 >= 2 && n % 10 <= 4 && (n % 100 < 12 || n % 100 > 14) ? "груза" : "грузов");
  // wireframe search(): apply the form, caption, toast with the count
  const search = (f) => {
    setDraft(f);
    setApplied(f);
  };
  const countFor = (f) => filterLoads(orders || [], f).length;
  const submit = (e) => {
    e.preventDefault();
    search(draft);
    const n = countFor(draft);
    say(`Показано ${n} ${grams(n)}`);
  };
  const reset = () => {
    if (q) setParams({ mode: "loads" }, { replace: true });
    search(EMPTY_FILTERS);
    const n = countFor(EMPTY_FILTERS);
    say(`Показано ${n} ${grams(n)}`);
  };

  const caption = `${applied.origin || "Любое отправление"} → ${applied.destination || "Любое направление"}`;

  // ⌘K: «Результаты поиска» — route + cargo per match
  useEffect(() => {
    const onSearch = (e) => {
      const term = String(e.detail?.q || "").trim().toLowerCase();
      if (!term) return;
      e.preventDefault();
      setDialog({ type: "results", rows: (orders || []).filter((o) => loadText(o).includes(term)) });
    };
    window.addEventListener("cab:global-search", onSearch);
    return () => window.removeEventListener("cab:global-search", onSearch);
  }, [orders]);

  const mapPoints = useMemo(
    () =>
      filtered.map((o) => ({
        lat: o.location_from?.lat,
        lng: o.location_from?.lng,
        city: cityOf(o.from_address) !== "—" ? o.from_address : null,
        title: `${o.from_address || ""} → ${o.to_address || ""}`,
        to: o.location_to?.lat != null && o.location_to?.lng != null ? { lat: Number(o.location_to.lat), lng: Number(o.location_to.lng) } : null,
        info: loadInfo(o, { done: responded(o), onRespond: () => setDialog({ type: "respond", order: o }), onDetails: () => setDialog({ type: "more", order: o }) }),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [filtered, matches],
  );
  const mapSummary = (
    <>
      <strong>
        <span>{filtered.length}</span> активных грузов
      </strong>
      <small>{caption}</small>
    </>
  );

  const loadSeries = useMemo(() => dailySeries(allOrders, (o) => o.created_at), [allOrders]);
  const companySeries = useMemo(() => dailySeries(companies, (c) => c.created_at), [companies]);
  const tiles = [
    {
      d: P.truck,
      tone: 0,
      value: orders ? nf(orders.length) : "…",
      label: "Активных грузов",
      series: loadSeries,
      seriesHint: "Новых грузов за 7 дней; процент — к предыдущим 7 дням",
    },
    companies.length > 0 && {
      d: P.box,
      tone: 1,
      value: nf(companies.length),
      label: "Компаний",
      series: companySeries,
      seriesHint: "Новых компаний за 7 дней; процент — к предыдущим 7 дням",
    },
    { d: P.user, tone: 2, value: orders ? nf(cities.length) : "…", label: "Городов" },
    metrics?.first_response_time_avg_seconds != null && {
      d: "M21 13a9 9 0 1 1-18 0 9 9 0 0 1 18 0 M12 7v6l4 3 M10 1h4",
      tone: 3,
      value: fmtDuration(metrics.first_response_time_avg_seconds),
      label: (
        <>
          Среднее время
          <br />
          до первого отклика
        </>
      ),
    },
  ];

  return (
    <CabPage name="loads">
      <main id="main">
        <SearchHero mode="loads" tiles={tiles} />

        <div className="loads-top">
          <section className="left-search">
            <div className="search-panel">
              <FilterTabs
                view="list"
                onParams={() => document.querySelector("#search-form input[name=origin]")?.focus()}
                onMap={() => setDialog({ type: "map" })}
                onList={() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}
              />
              <form id="search-form" onSubmit={submit}>
                <SearchFields form={draft} setForm={setDraft} kind="loads" />
                <SearchFooter onReset={reset} submit="Найти грузы" />
              </form>
            </div>
          </section>
          <MapPanel
            ariaLabel={`Карта грузов: ${caption}`}
            points={mapPoints}
            summary={mapSummary}
            icon={P.truck}
            wrap
            expandLabel="Показать все грузы на карте"
            onExpand={() => setDialog({ type: "map" })}
          />
        </div>

        <section className="results" ref={resultsRef}>
          <div className="results-heading">
            <div>
              <h2>
                Найдено <span id="result-count">{orders ? filtered.length : "…"}</span> грузов
              </h2>
              <p id="route-caption">{caption}</p>
              {hid.count ? (
                <button className="hidden-rows" type="button" onClick={hid.showAll}>
                  Показать скрытые ({hid.count})
                </button>
              ) : null}
            </div>
            <div className="views" role="group" aria-label="Вид результатов">
              <button className="active" type="button" onClick={() => resultsRef.current?.scrollIntoView({ behavior: "smooth", block: "start" })}>
                <Icon d={VIEW_ICONS.list} />
                Списком
              </button>
              <button type="button" onClick={() => setDialog({ type: "map" })}>
                <Icon d={P.map} />
                На карте
              </button>
            </div>
            <label className="sort">
              <span>Сортировка:</span>
              <select aria-label="Сортировка" id="sort" value={sort} onChange={(e) => setSort(e.target.value)}>
                <option value="new">Сначала новые</option>
                <option value="rate">По ставке</option>
                <option value="date">По дате погрузки</option>
              </select>
              <Icon d={P.chevron} />
            </label>
          </div>
          <div className="table-container">
            <table>
              <colgroup>
                <col style={{ width: "14%" }} />
                <col style={{ width: "15.6%" }} />
                <col style={{ width: "9%" }} />
                <col style={{ width: "13.3%" }} />
                <col style={{ width: "9.8%" }} />
                <col style={{ width: "17.7%" }} />
                <col style={{ width: "20.6%" }} />
              </colgroup>
              <thead>
                <tr>
                  <th>Маршрут</th>
                  <th>Груз</th>
                  <th>Вес / Объём</th>
                  <th>Дата погрузки</th>
                  <th>Ставка</th>
                  <th>Контакт</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((o) => {
                  const w = o.item?.weight;
                  const v = o.item?.volume;
                  // route length in km from `distance` ("330 км", "1 200 км")
                  const km = parseFloat(String(o.distance || "").replace(/\s/g, "").replace(",", ".")) || 0;
                  const company = o.contacts?.company_name || o.contacts?.contact_name || "—";
                  const phone = o.contacts?.phone_number;
                  const typeName = ORDER_TYPES[o.item?.type] || "Груз";
                  const sameDay = !o.to_date || o.to_date === o.from_date;
                  const done = responded(o);
                  const prof = byOwner[String(o.owner)];
                  return (
                    <tr key={o.uuid}>
                      <td data-label="Маршрут">
                        <div className="route">
                          <span className="route-line"></span>
                          <div>
                            <strong>{cityOf(o.from_address)}</strong>
                            <strong>{cityOf(o.to_address)}</strong>
                            {o.distance ? <small>{o.distance}</small> : null}
                          </div>
                        </div>
                      </td>
                      <td data-label="Груз">
                        <div className="cargo">
                          <span className="cargo-icon">
                            <Icon d={P.box} />
                          </span>
                          <div>
                            <strong>{typeName}</strong>
                            <small>{bodyTypeName(o.transport_body_type) || "—"}</small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Вес / Объём">
                        <div className="weight">
                          <Icon d={P.box} />
                          <div>
                            <strong>{w != null ? `${nf(w)} т` : "—"}</strong>
                            <small>{v != null ? `${nf(v)} м³` : "—"}</small>
                          </div>
                        </div>
                      </td>
                      <td data-label="Дата погрузки">
                        <strong>{fmtDateShort(o.from_date)}</strong>
                        {!sameDay ? <small>Загрузка до {fmtDateShort(o.to_date)}</small> : null}
                      </td>
                      <td data-label="Ставка">
                        {o.price ? (
                          <>
                            <b className="load-rate">{money(o.price, o.currency)}</b>
                            {km ? <small>{`${nf(Math.round(o.price / km))} ${currencyLabel(o.currency)}/км`}</small> : null}
                          </>
                        ) : (
                          <b className="load-rate">Торг</b>
                        )}
                      </td>
                      <td data-label="Контакт">
                        <div className="contact">
                          <CompanyLogo src={prof?.profile_images} />
                          <div>
                            <strong>
                              {company}
                              {prof?.verified ? (
                                <span className="verified" role="img" aria-label="Проверен" title="Проверенная компания">
                                  ✓
                                </span>
                              ) : null}
                            </strong>
                            {COMPANY_ROLES[prof?.role] ? <span className="source-tag">{COMPANY_ROLES[prof.role]}</span> : null}
                            {phone ? <a href={`tel:${phone.replace(/[^\d+]/g, "")}`}>{phone}</a> : null}
                          </div>
                        </div>
                      </td>
                      <td data-label="Действия">
                        <div className="load-actions">
                          <div>
                            <button className="button primary respond" type="button" disabled={done} onClick={() => setDialog({ type: "respond", order: o })}>
                              {done ? "Отклик отправлен" : "Откликнуться"}
                            </button>
                          </div>
                          <button aria-label="Действия с грузом" aria-haspopup="menu" className="button more" title="Действия с грузом" type="button" onClick={(e) => rowMenu.openMenu(e, o)}>
                            ···
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {orders && filtered.length === 0 && (
              <div className="empty">
                <h3>Подходящих грузов не найдено</h3>
                <p>Измените параметры поиска.</p>
                <button className="button" type="button" onClick={reset}>
                  Сбросить фильтры
                </button>
              </div>
            )}
            {!orders && !error && (
              <div className="empty">
                <h3>Загрузка…</h3>
              </div>
            )}
            {error && (
              <div className="empty">
                <h3>{error}</h3>
              </div>
            )}
          </div>
        </section>
      </main>
      <MobileNav />

      {rowMenu.menu && (
        <RowMenu
          anchor={rowMenu.menu.anchor}
          items={[
            { label: "Открыть детали", onClick: () => setDialog({ type: "more", order: rowMenu.menu.payload }) },
            { label: "Скрыть из результатов", onClick: () => hid.hide(rowMenu.menu.payload.uuid) },
          ]}
          onClose={rowMenu.closeMenu}
        />
      )}
      {dialog?.type === "map" && (
        <Modal title="Грузы на карте" wide full onClose={() => setDialog(null)}>
          <MapPanel ariaLabel={`Карта грузов: ${caption}`} points={mapPoints} summary={mapSummary} icon={P.truck} wrap />
        </Modal>
      )}
      {dialog?.type === "more" && (
        <LoadDetailsPanel
          order={dialog.order}
          onClose={() => setDialog(null)}
          footer={
            <>
              <button
                className="button primary"
                disabled={responded(dialog.order)}
                onClick={() => setDialog({ type: "respond", order: dialog.order })}
                type="button"
              >
                {responded(dialog.order) ? "Предложение отправлено" : "Предложить транспорт"}
              </button>
              <button className="button" onClick={() => setDialog(null)} type="button">
                Закрыть
              </button>
            </>
          }
        />
      )}
      {dialog?.type === "results" && (
        <Modal title="Результаты поиска" onClose={() => setDialog(null)}>
          {dialog.rows.length ? (
            dialog.rows.map((o) => (
              <p key={o.uuid}>
                {routeOf(o)}
                <br />
                {ORDER_TYPES[o.item?.type] || "Груз"}
                {o.comment ? ` · ${o.comment}` : ""}
              </p>
            ))
          ) : (
            <p>Совпадений нет.</p>
          )}
        </Modal>
      )}
      {dialog?.type === "respond" && (
        <RespondPanel
          order={dialog.order}
          user={user}
          carrier={carrier}
          matchedVehicleIds={matchFor(dialog.order).filter((m) => !m.decided).map((m) => Number(m.vehicle_id))}
          onClose={() => setDialog(null)}
          onDone={(n, failed) => {
            say(failed ? `Отклик отправлен: ${n}, не удалось: ${failed}` : n > 1 ? `Отклик отправлен (${n} машины)` : "Отклик отправлен");
            loadMatches();
          }}
        />
      )}
      <datalist id="city-options">
        {cities.map((c) => (
          <option key={c} value={c} />
        ))}
      </datalist>
      <div id="toast" role="status" aria-live="polite" className={toast ? "visible" : ""}>
        {toast}
      </div>
    </CabPage>
  );
}
